import { NgClass } from '@angular/common';
import {
  Component,
  inject
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { NgxTolgeeModule } from '@tolgee/ngx';

import { Template } from '../../../../../core/template';
import { Error } from '../../core/error';
import { NotificationService } from '../../notification/notification.service';
import { TelInputComponent } from '../../shared/components/tel-input/tel-input.component';
import { HoverGradientDirective } from '../../shared/directives/hover-gradient.directive';
import { ProgressClickDirective } from '../../shared/directives/progress-click.directive';
import { AuthService } from '../../shared/services/auth.service';
import { ContactService } from '../../shared/services/contact.service';
import { GlobalService } from '../../shared/services/globals.service';
import { EmailValidator } from '../../shared/validators/validators';

type ContactForm = {
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  entity: FormControl<string>;
  subject: FormControl<string>;
  email: FormControl<string>;
  phone: FormControl<string>;
  acceptTerms: FormControl<boolean>;
};

@Component({
  selector: 'app-press-contact',
  imports: [
    HoverGradientDirective,
    NgxTolgeeModule,
    NgClass,
    ReactiveFormsModule,
    ProgressClickDirective,
    TelInputComponent
  ],
  templateUrl: './contact.component.html'
})
export class ContactComponent {
  authService = inject(AuthService);
  notificationService = inject(NotificationService);
  contactService = inject(ContactService);
  globalService = inject(GlobalService);

  form: FormGroup<ContactForm>;

  constructor() {
    const user = this.authService.user();
    this.form = new FormGroup({
      firstName: new FormControl(user?.firstName ?? '', { nonNullable: true, validators: [ Validators.required ] }),
      lastName: new FormControl(user?.lastName ?? '', { nonNullable: true, validators: [ Validators.required ] }),
      entity: new FormControl('', { nonNullable: true, validators: [ Validators.required ] }),
      subject: new FormControl('', { nonNullable: true, validators: [ Validators.required ] }),
      email: new FormControl(user?.email ?? '', {
        nonNullable: true,
        validators: [ Validators.required, EmailValidator ]
      }),
      phone: new FormControl(user?.phone ?? '', {
        nonNullable: true, validators: [ Validators.minLength(12) ]
      }),
      acceptTerms: new FormControl(user?.authCompleted ?? false, {
        nonNullable: true,
        validators: [ Validators.requiredTrue ]
      }),
    })
  }

  get acceptTerms(): boolean {
    return this.form.get('acceptTerms')?.value ?? false;
  }

  set acceptTerms(value: boolean) {
    this.form.get('acceptTerms')?.setValue(value);
  }

  submit = async (): Promise<void> => {
    const data = this.form.getRawValue();
    try {
      await this.contactService.contact(
        this.globalService.settings().contacts,
        Template.PressEmail,
        data);
      this.notificationService.open({
        type: 'success',
        title: 'mailContacted',
        message: 'mailContactedDescription'
      });
      this.form.reset();
    } catch (err) {
      const error = err as Error;
      this.notificationService.open({ type: 'error', title: error.details.code, message: error.message });
    }
  }
}
